import React from 'react';
import './ZoneInfo.css';


interface ZoneInfoProps {
  zoneProperties: any | null;
  interactionType?: 'mapClick' | 'addressSearch';
  parcelId?: string;
  point?: { lat: number, lng: number, type: 'mapClick' | 'addressSearch' } | null;
  parcelAreaFt2?: number | null;
  displayUnit?: 'ft' | 'm';
}

const ZoneInfo: React.FC<ZoneInfoProps> = ({
  zoneProperties,
  interactionType = 'mapClick',
  parcelId,
  point,
  parcelAreaFt2,
  displayUnit = 'ft',
}) => {
  if (!zoneProperties) {
    return (
      <div className="panel-card">
        <div className="panel-card-header">
          <div className="panel-card-header-content">
            <h3 className="panel-card-title">Zoning Information</h3>
            <p className="panel-card-subtitle">No location selected</p>
          </div>
        </div>
        <div className="panel-card-content">
          <div className="empty-state">
            <div className="empty-state-icon">📍</div>
            <div className="empty-state-text">
              Click on the map or search for an address to view zoning information
            </div>
          </div>
        </div>
      </div>
    );
  }

  const determineZoneType = (zoneCode: string, zoneLabel?: string): string => {
    if (!zoneCode) return 'Unknown';

    // Check for Former City By-law Areas
    if (zoneCode === 'Former' || (zoneLabel && zoneLabel.includes('Former City'))) {
      return 'Former City By-law Area';
    }

    if (zoneLabel && zoneLabel.includes('Former City of Toronto')) {
      return 'Former City of Toronto By-law Area';
    }

    const code = zoneCode.toUpperCase();
    
    // Commercial Residential Employment (check first as it contains CR)
    if (code.startsWith('CRE')) return 'Commercial Residential Employment';
    
    // Commercial Residential (check before general Commercial)
    if (code.startsWith('CR')) return 'Commercial Residential';
    
    // Residential Apartment zones (check before general Residential)
    if (code.startsWith('RA') || code.startsWith('RM')) return 'Residential Apartment';
    
    // Residential zones
    if (code.startsWith('R')) return 'Residential';
    
    // Commercial zones
    if (code.startsWith('C')) return 'Commercial';
    
    // Employment/Industrial zones
    if (code.startsWith('E') || code.startsWith('EI')) return 'Employment Industrial';
    
    // Institutional zones
    if (code.startsWith('I')) return 'Institutional';
    
    // Open Space zones
    if (code.startsWith('O') || code.startsWith('OS')) return 'Open Space';
    
    // Utility and Transportation zones
    if (code.startsWith('U') || code.startsWith('UT')) return 'Utility and Transportation';

    return 'Other';
  };

  const getBylawChapterLink = (zoneCode: string): string => {
    if (!zoneCode) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Index.htm';

    // Handle Former City By-law Areas
    if (zoneCode === 'Former') {
      return 'https://www.toronto.ca/city-government/planning-development/zoning-by-law-preliminary-zoning-reviews/zoning-by-law-569-2013-2/zoning-by-law-research/zoning-former-city-of-toronto/';
    }

    if (/^R[A-Z]?/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter10.htm';
    if (/^RA[A-Z]?/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter15.htm';
    if (/^C[A-Z]?/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter40.htm';
    if (/^CR[A-Z]?/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter50.htm';
    if (/^E[A-Z]?/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter60.htm';
    if (/^I[A-Z]?/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter80.htm';
    if (/^O[A-Z]?/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter90.htm';
    if (/^UT/.test(zoneCode)) return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Chapter100.htm';

    return 'https://www.toronto.ca/zoning/bylaw_amendments/ZBL_NewProvision_Index.htm';
  };

  // Improved Former City Area Detection
  const isFormerCityArea = (
    // Check our new isFormerCityArea flag from the boundary detection
    zoneProperties.isFormerCityArea ||
    (zoneProperties.ZN_ZONE === 'Former') ||
    // Check for explicit former city indicators
    (zoneProperties.FORMER_CODE && zoneProperties.FORMER_CODE !== 'null') ||
    (zoneProperties.ZONE_LABEL && zoneProperties.ZONE_LABEL.includes('Former')) ||
    (zoneProperties.ZN_STRING && zoneProperties.ZN_STRING.includes('Former')) ||
    (zoneProperties.DESCRIPTION && zoneProperties.DESCRIPTION.includes('Former')) ||
    // Check for empty or null zone codes which often indicate former city areas
    (!zoneProperties.ZN_ZONE && !zoneProperties.ZONE_CODE) ||
    // Check for specific former city zone patterns
    (zoneProperties.ZN_ZONE === '' || zoneProperties.ZN_ZONE === null)
  );

  // Extract the actual data more precisely from the zoneProperties object
  
  // Extract zone code - prioritize the correct properties from the GeoJSON data
  let extractedZoneCode = '';
  
  // Check if this is a former city area first
  if (isFormerCityArea) {
    extractedZoneCode = 'Former City By-law';
  } else {
    // For modern zoning areas, extract the zone code from standard properties
    extractedZoneCode = zoneProperties.ZONE_CODE || 
      zoneProperties.zone_code || 
      zoneProperties.ZONECODE ||
      zoneProperties.ZC || 
      'Unknown';
  }
  
  // Extract complete zone label with suffixes and exceptions
  const extractedZoneLabel = zoneProperties.ZN_STRING || 
    zoneProperties.ZONE_LABEL ||
    zoneProperties.RD || 
    zoneProperties.DESCRIPTION ||
    zoneProperties.DESC ||
    extractedZoneCode || // Fallback to just the zone code
    '';
  
  // Determine zone type based on the base zone code (extract base from full zone string)
  let extractedZoneType = '';
  if (isFormerCityArea) {
    extractedZoneType = 'Former City By-law Area';
  } else {
    const baseZoneCode = extractedZoneCode.replace(/\s*\([^)]*\)/g, '').trim(); // Remove parentheses content
    extractedZoneType = determineZoneType(baseZoneCode, extractedZoneLabel);
  }
  
  // Set the final values
  const displayZoneCode = extractedZoneCode;
  const zoneLabel = extractedZoneLabel;
  const zoneType = extractedZoneType;
  const bylawChapterLink = getBylawChapterLink(extractedZoneCode);
  
  // Fail-safe: ensure we always have some content to display
  const safeZoneCode = displayZoneCode || 'Unknown';
  const safeZoneType = zoneType || 'Unknown';
  const safeZoneLabel = zoneLabel || safeZoneCode;

  // Special UI for Former City By-law areas
  if (isFormerCityArea) {
    return (
      <div className="panel-card">
        <div className="panel-card-header" style={{ backgroundColor: '#f8f8f8', borderLeft: '4px solid #999' }}>
          <div className="panel-card-header-content">
            <h3 className="panel-card-title" style={{ color: '#666' }}>Former City By-law Area</h3>
            <p className="panel-card-subtitle" style={{ color: '#777' }}>This area follows former city regulations</p>
          </div>
        </div>
        <div className="panel-card-content">
          <div style={{ 
            padding: '16px',
            fontSize: '14px',
            color: '#555',
            lineHeight: '1.5',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 12px 0' }}>
              This area follows Former City of Toronto by-laws. Contact City Planning for specific requirements.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-card">
      <div className="panel-card-header">
        <div className="panel-card-header-content">
          <h3 className="panel-card-title">Zoning Information</h3>
          <p className="panel-card-subtitle">Property zoning details</p>
        </div>
      </div>
      <div className="panel-card-content">
        <div className="zone-info-cards">
          <div className="zone-info-card">
            <div className="zone-info-label">Zone Code</div>
            <div className="zone-info-value">{safeZoneCode}</div>
          </div>
          <div className="zone-info-card">
            <div className="zone-info-label">Zone Type</div>
            <div className="zone-info-value">{safeZoneType}</div>
          </div>
          <div className="zone-info-card zone-info-card-wide">
            <div className="zone-info-label">Zone Label</div>
            <div className="zone-info-value">{safeZoneLabel}</div>
          </div>
          {typeof parcelAreaFt2 === 'number' && !parcelId && (
            <div className="zone-info-card zone-info-card-wide">
              <div className="zone-info-label">Parcel Area</div>
              <div className="zone-info-value">
                {displayUnit === 'ft'
                  ? `${parcelAreaFt2.toLocaleString()} ft²`
                  : `${(parcelAreaFt2 * 0.092903).toFixed(0)} m²`}
              </div>
            </div>
          )}
          
          {/* Exception and overlay information as additional cards */}
          {(() => {
            // Extract exception number from zone string (e.g., "RD (f15.0; a550) (x5)" -> "5")
            const exceptionMatch = extractedZoneLabel.match(/\(x(\d+)\)/);
            const exceptionNumber = exceptionMatch ? exceptionMatch[1] : null;
            
            // Check for overlay information
            const overlayInfo = zoneProperties.OVERLAY || zoneProperties.HEIGHT_OVERLAY || zoneProperties.COVERAGE_OVERLAY;
            
            return (
              <>
                {exceptionNumber && (
                  <div className="zone-info-card">
                    <div className="zone-info-label">Exception</div>
                    <div className="zone-info-value">Exception {exceptionNumber}</div>
                  </div>
                )}
                {overlayInfo && (
                  <div className="zone-info-card">
                    <div className="zone-info-label">Overlay</div>
                    <div className="zone-info-value">{overlayInfo}</div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
        <div className="zone-actions">
          <a
            href={bylawChapterLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-bylaw"
          >
            View Zoning Bylaw
          </a>
        </div>
      </div>

    </div>
  );
};

export default ZoneInfo;
